import { Head, Html, Main, NextScript } from 'next/document';
import Script from 'next/script';
import type { ReactNode } from 'react';

type GetAnchorageDocumentOptions = {
  nonce: string;
  additionalScripts?: ReactNode;
  /**
   * Classes rendered on the `<html>` element. Pass the design-system theme
   * class (e.g. `anchorage-dark`) so token-based styles apply before the
   * client-side theme hook runs.
   */
  htmlClassName?: string;
};

/**
 * Factory that creates a Next.js custom Document component with shared
 * Anchorage conventions: CSP nonce, env.js script loading.
 *
 * @example
 * ```tsx
 * // apps/backoffice/src/pages/_document.tsx
 * import { getAnchorageDocument } from '@anchorage/next';
 * import nonce from '@anchorage/backoffice/components/NextDocument/nonce.json';
 *
 * export default getAnchorageDocument({
 *   nonce: nonce.nonce,
 *   additionalScripts: <script async src="/hash-router-redirect.js" />,
 * });
 * ```
 */
export const getAnchorageDocument = ({
  nonce,
  additionalScripts,
  htmlClassName,
}: GetAnchorageDocumentOptions) =>
  function Document() {
    return (
      <Html className={htmlClassName}>
        {/* Nonce should not be hardcoded — see https://linear.app/anchorlabs/issue/CEXP-2136/dynamic-nonce-for-csp */}
        <Head nonce={nonce} />
        <body>
          <Main />
          <NextScript nonce={nonce} />
          <Script src="/env.js" strategy="beforeInteractive" />
          {additionalScripts}
        </body>
      </Html>
    );
  };
